import { createFileRoute, Link } from "@tanstack/react-router";
import { AddCard, CardGrid, ContentCard, IconButton } from "@op/sf-editor";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Trans, useLingui } from "@lingui/react/macro";
import { useState } from "react";
import { LanguageCreateDialog } from "../../../../components/app/project/LanguageCreateDialog";
import { useDisable } from "../../../../hooks/useDisable";
import { languageMap } from "../../../../utils/language";
import { getProjectLanguages } from "@/queries/project";
import z from "zod";
import { FeatherSettings } from "@subframe/core";

const paramSchema = z.object({
  organizationSlug: z.string(),
  projectSlug: z.string(),
});

export const Route = createFileRoute("/app/$organizationSlug/$projectSlug/")({
  params: paramSchema,
  loader: async ({ params: { organizationSlug, projectSlug } }) => {
    const languages = await getProjectLanguages(organizationSlug, projectSlug);
    return { languages };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const [languageCreateDialogOpen, setLanguageCreateDialogOpen] =
    useState<boolean>(false);

  const { organizationSlug, projectSlug } = Route.useParams();

  const { languages } = Route.useLoaderData();

  const queryClient = useQueryClient();

  const { t } = useLingui();

  const { data } = useQuery(
    {
      queryKey: ["editor-project-languages", { organizationSlug, projectSlug }],
      initialData: languages,
      queryFn: () => getProjectLanguages(organizationSlug, projectSlug),
    },
    queryClient,
  );

  const languageCreateButtonDisabled = useDisable(
    organizationSlug,
    projectSlug,
    ["admin", "publisher", "editor"],
  );

  return (
    <div className="flex h-full w-full max-w-[1280px] flex-col items-start gap-8 px-4 mx-auto mt-12">
      <div className="flex w-full flex-col items-start gap-6">
        <div className="flex flex-col items-start">
          <span className="text-caption font-caption text-subtext-color">
            {data.project.organizationName} - {data.project.projectName}
          </span>
          <span className="text-heading-1 font-heading-1 text-default-font">
            <Trans>Languages</Trans>
          </span>
        </div>
        <CardGrid>
          {data.languages.map((language) => (
            <ContentCard
              key={language.id}
              settingsSlot={
                <Link
                  to="/app/$organizationSlug/$projectSlug/$languageKey/settings"
                  params={{
                    organizationSlug,
                    projectSlug,
                    languageKey: language.key,
                  }}
                  className="contents"
                >
                  <IconButton
                    variant="brand-tertiary"
                    size="medium"
                    icon={<FeatherSettings />}
                  />
                </Link>
              }
            >
              <Link
                to="/app/$organizationSlug/$projectSlug/$languageKey"
                params={{
                  organizationSlug,
                  projectSlug,
                  languageKey: language.key,
                }}
                className="contents"
              >
                <ContentCard.Label>
                  <Trans>Language</Trans>
                </ContentCard.Label>
                <ContentCard.Title>
                  {languageMap[language.key]}
                </ContentCard.Title>
              </Link>
            </ContentCard>
          ))}
          {!languageCreateButtonDisabled && (
            <AddCard
              label={t`Add a new language...`}
              onClick={() => setLanguageCreateDialogOpen(true)}
            />
          )}
        </CardGrid>
      </div>
      <LanguageCreateDialog
        open={languageCreateDialogOpen}
        onOpenChange={setLanguageCreateDialogOpen}
        organizationSlug={organizationSlug}
        projectSlug={projectSlug}
        usedLanguages={data?.languages?.map((language) => language.key) || []}
      />
    </div>
  );
}
